import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import logo from "./assets/logofinal-thmb.png";

const LANG_KEY = "weavevo_lang";

const FOOTER_I18N = {
  en: {
    legal: "Legal",
    legal_notice: "Legal notice",
    privacy: "Privacy policy",
    terms: "Terms of use",
    cookies: "Cookie policy",
    accessibility: "Accessibility & GDPR",

    discover: "Discover",
    explore_media: "Explore media",
    about: "About",
    blog: "Blog / News",
    faq: "FAQ / Help",

    support: "Support",
    contact: "Contact / Support",
    help_center_soon: "Help center (soon)",
    report_soon: "Report content (soon)",

    social: "Social",
    instagram: "Instagram",
    youtube: "YouTube",
    discord: "Discord",
    x: "X",

    brand: "Weavevo",
    rights: "All rights reserved.",
    copyright: "© {year} Weavevo. {rights}",
  },
  fr: {
    legal: "Légal",
    legal_notice: "Mentions légales",
    privacy: "Politique de confidentialité",
    terms: "Conditions d’utilisation (CGU)",
    cookies: "Politique de cookies",
    accessibility: "Accessibilité & RGPD",

    discover: "Découvrir",
    explore_media: "Découvrir les médias",
    about: "À propos",
    blog: "Blog / Actualités",
    faq: "FAQ / Aide",

    support: "Support",
    contact: "Contact / Support",
    help_center_soon: "Centre d’aide (bientôt)",
    report_soon: "Signaler un contenu (bientôt)",

    social: "Réseaux",
    instagram: "Instagram",
    youtube: "YouTube",
    discord: "Discord",
    x: "X",

    brand: "Weavevo",
    rights: "Tous droits réservés.",
    copyright: "© {year} Weavevo. {rights}",
  },
};

function getLang() {
  if (typeof window === "undefined") return "en";
  const v = (localStorage.getItem(LANG_KEY) || "en").toLowerCase();
  return v === "fr" ? "fr" : "en";
}

function format(str, vars) {
  if (!vars) return str;
  return Object.keys(vars).reduce((acc, k) => acc.replaceAll(`{${k}}`, String(vars[k])), str);
}

const WeavevoMark = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 64 64"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <linearGradient id="wv_g" x1="8" y1="8" x2="56" y2="56" gradientUnits="userSpaceOnUse">
        <stop stopColor="rgba(99,102,241,1)" />
        <stop offset="1" stopColor="rgba(56,189,248,1)" />
      </linearGradient>
    </defs>
    <path
      d="M10 18c0-4.4 3.6-8 8-8h6c4.4 0 8 3.6 8 8v7.5c0 2.3 1.2 4.4 3.1 5.6l3.8 2.3c2 1.2 3.1 3.3 3.1 5.6V46c0 4.4-3.6 8-8 8h-6c-4.4 0-8-3.6-8-8v-7.5c0-2.3-1.2-4.4-3.1-5.6l-3.8-2.3C11.2 29.4 10 27.3 10 25v-7Z"
      stroke="url(#wv_g)"
      strokeWidth="4.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity="0.92"
    />
    <path
      d="M20 22h8M36 42h8"
      stroke="url(#wv_g)"
      strokeWidth="4.5"
      strokeLinecap="round"
      opacity="0.9"
    />
  </svg>
);


const FooterRunwayStyle = () => {
  const [lang, setLang] = useState(getLang);

  useEffect(() => {
    const sync = () => setLang(getLang());
    const onStorage = (e) => {
      if (e && e.key && e.key !== LANG_KEY) return;
      sync();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("weavevo_lang_changed", sync);
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", sync);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("weavevo_lang_changed", sync);
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, []);

  const dict = useMemo(() => FOOTER_I18N[lang] || FOOTER_I18N.en, [lang]);
  const t = (k, vars) => format(dict[k] ?? FOOTER_I18N.en[k] ?? k, vars);

  return (
    <footer className="bg-black text-white pt-5 pb-4 mt-5">
      <div className="container">
        <div className="row row-cols-2 row-cols-md-4 g-4">
          {/* Col 1: Legal */}
          <div>
            <h6 className="mb-3 d-flex align-items-center gap-2" style={{ fontWeight: 800, letterSpacing: ".02em" }}>
              <i className="bi bi-shield-check" />
              <span>{t("legal")}</span>
            </h6>
            <ul className="list-unstyled small m-0">
              <li className="mb-2">
                <Link className="text-white-50 text-decoration-none d-inline-flex align-items-center gap-2" to="/mentions-legales">
                  <i className="bi bi-file-earmark-text" />
                  <span>{t("legal_notice")}</span>
                </Link>
              </li>
              <li className="mb-2">
                <Link className="text-white-50 text-decoration-none d-inline-flex align-items-center gap-2" to="/politique-confidentialite">
                  <i className="bi bi-fingerprint" />
                  <span>{t("privacy")}</span>
                </Link>
              </li>
              <li className="mb-2">
                <Link className="text-white-50 text-decoration-none d-inline-flex align-items-center gap-2" to="/cgu">
                  <i className="bi bi-journal-text" />
                  <span>{t("terms")}</span>
                </Link>
              </li>
              <li className="mb-2">
                <Link className="text-white-50 text-decoration-none d-inline-flex align-items-center gap-2" to="/politique-cookies">
                  <i className="bi bi-cookie" />
                  <span>{t("cookies")}</span>
                </Link>
              </li>
              <li>
                <Link className="text-white-50 text-decoration-none d-inline-flex align-items-center gap-2" to="/accessibilite-rgpd">
                  <i className="bi bi-universal-access" />
                  <span>{t("accessibility")}</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 2: Discover */}
          <div>
            <h6 className="mb-3 d-flex align-items-center gap-2" style={{ fontWeight: 800, letterSpacing: ".02em" }}>
              <i className="bi bi-compass" />
              <span>{t("discover")}</span>
            </h6>
            <ul className="list-unstyled small m-0">
              <li className="mb-2">
                <Link className="text-white-50 text-decoration-none d-inline-flex align-items-center gap-2" to="/decouvrir">
                  <i className="bi bi-grid" />
                  <span>{t("explore_media")}</span>
                </Link>
              </li>
              <li className="mb-2">
                <Link className="text-white-50 text-decoration-none d-inline-flex align-items-center gap-2" to="/a-propos">
                  <i className="bi bi-info-circle" />
                  <span>{t("about")}</span>
                </Link>
              </li>
              <li className="mb-2">
                <Link className="text-white-50 text-decoration-none d-inline-flex align-items-center gap-2" to="/blog">
                  <i className="bi bi-newspaper" />
                  <span>{t("blog")}</span>
                </Link>
              </li>
              <li>
                <Link className="text-white-50 text-decoration-none d-inline-flex align-items-center gap-2" to="/faq">
                  <i className="bi bi-question-circle" />
                  <span>{t("faq")}</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3: Support */}
          <div>
            <h6 className="mb-3 d-flex align-items-center gap-2" style={{ fontWeight: 800, letterSpacing: ".02em" }}>
              <i className="bi bi-life-preserver" />
              <span>{t("support")}</span>
            </h6>
            <ul className="list-unstyled small m-0">
              <li className="mb-2">
                <Link className="text-white-50 text-decoration-none d-inline-flex align-items-center gap-2" to="/contact">
                  <i className="bi bi-envelope" />
                  <span>{t("contact")}</span>
                </Link>
              </li>
              <li className="mb-2">
                <span className="text-white-50 d-inline-flex align-items-center gap-2">
                  <i className="bi bi-book" />
                  <span>{t("help_center_soon")}</span>
                </span>
              </li>
              <li>
                <span className="text-white-50 d-inline-flex align-items-center gap-2">
                  <i className="bi bi-flag" />
                  <span>{t("report_soon")}</span>
                </span>
              </li>
            </ul>
          </div>

          {/* Col 4: Social */}
          <div>
            <h6 className="mb-3 d-flex align-items-center gap-2" style={{ fontWeight: 800, letterSpacing: ".02em" }}>
              <i className="bi bi-broadcast" />
              <span>{t("social")}</span>
            </h6>
            <ul className="list-unstyled small m-0">
              <li className="mb-2">
                <span className="text-white-50 d-inline-flex align-items-center gap-2">
                  <i className="bi bi-instagram" />
                  <span>{t("instagram")}</span>
                </span>
              </li>
              <li className="mb-2">
                <span className="text-white-50 d-inline-flex align-items-center gap-2">
                  <i className="bi bi-youtube" />
                  <span>{t("youtube")}</span>
                </span>
              </li>
              <li className="mb-2">
                <span className="text-white-50 d-inline-flex align-items-center gap-2">
                  <i className="bi bi-discord" />
                  <span>{t("discord")}</span>
                </span>
              </li>
              <li>
                <span className="text-white-50 d-inline-flex align-items-center gap-2">
                  <i className="bi bi-twitter" />
                  <span>{t("x")}</span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-4 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center border-top border-secondary pt-3 gap-2">
          <span className="d-inline-flex align-items-center gap-2" style={{ fontWeight: 850, letterSpacing: ".02em" }}>
           <div id="kc-header" className="login-pf-header text-center mb-3">
                     <img src={logo} alt={t("logo_alt")} style={{ height: 100 }} />
                   </div>
            <span>{t("brand")}</span>
          </span>
          <small className="fw-semibold">{t("copyright", { year: new Date().getFullYear(), rights: t("rights") })}</small>
        </div>
      </div>
    </footer>
  );
};

export default FooterRunwayStyle;
